import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { api } from "../services/api";
import CmsEditableText from "./cms/CmsEditableText";
import CmsEditBar from "./cms/CmsEditBar";
import { useCmsPage } from "../hooks/useCmsPage";
import { useWishlistActions } from "../hooks/useWishlistActions";
import { resolveProductImage } from "../utils/productAssets";
import { formatCount } from "../utils/formatCount";
import { getDisplaySoldCount } from "../utils/displaySoldCount";

const TABS = [
  { key: "all", label: "All products" },
  { key: "featured", label: "Featured products" },
  { key: "popular", label: "Popular products" },
  { key: "mostViewed", label: "Most viewed products" },
  { key: "topSelling", label: "Top selling" },
];

const emptyPagination = {
  page: 1,
  limit: 25,
  total: 0,
  pages: 1,
  hasNextPage: false,
  hasPrevPage: false,
};

function AllProductsSection() {
  const cms = useCmsPage("products", {
    header: {
      eyebrow: "All Products",
      headline: "Featured, popular, most viewed, and top selling products",
      subtitle: "Browse Bobbin USA industrial sewing parts, filter by category, and search by SKU, part name, or brand.",
    },
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("section") || "all");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [pagination, setPagination] = useState(emptyPagination);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreSentinelRef = useRef(null);
  const categoryFilter = searchParams.get("category") || "";
  const categorySlugFilter = searchParams.get("categorySlug") || "";
  const subcategorySlugFilter = searchParams.get("subcategorySlug") || "";
  const brandLineFilter = searchParams.get("brandLine") || "";

  useEffect(() => {
    setSearch(searchParams.get("search") || "");
    setDebouncedSearch(searchParams.get("search") || "");
    setPage(Number(searchParams.get("page")) || 1);
    setActiveTab(searchParams.get("section") || "all");
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;
    api.get("/categories")
      .then((data) => {
        if (mounted) setCategories(data.categories || []);
      })
      .catch(() => {
        if (mounted) setCategories([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", "30");
    params.set("page", String(page));
    if (activeTab !== "all") params.set("section", activeTab);
    if (categoryFilter) params.set("category", categoryFilter);
    if (categorySlugFilter) params.set("categorySlug", categorySlugFilter);
    if (subcategorySlugFilter) params.set("subcategorySlug", subcategorySlugFilter);
    if (brandLineFilter) params.set("brandLine", brandLineFilter);
    if (debouncedSearch) params.set("search", debouncedSearch);
    return params.toString();
  }, [activeTab, brandLineFilter, categoryFilter, categorySlugFilter, debouncedSearch, page, subcategorySlugFilter]);

  const selectedCategory = useMemo(() => {
    if (!categorySlugFilter) return null;
    return categories.find((category) => category.slug === categorySlugFilter) || null;
  }, [categories, categorySlugFilter]);

  const selectedSubcategory = useMemo(() => {
    if (!subcategorySlugFilter || !selectedCategory) return null;
    return (selectedCategory.subcategories || []).find((subcategory) => subcategory.slug === subcategorySlugFilter) || null;
  }, [selectedCategory, subcategorySlugFilter]);

  useEffect(() => {
    let mounted = true;
    const isFirstPage = page === 1;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);
    api.get(`/listings?${queryString}`)
      .then((data) => {
        if (!mounted) return;
        const nextProducts = Array.isArray(data.products) ? data.products : [];
        setProducts((current) => {
          if (isFirstPage) return nextProducts;
          const seen = new Set(current.map((product) => product._id || product.sku));
          return [...current, ...nextProducts.filter((product) => !seen.has(product._id || product.sku))];
        });
        setPagination(data.pagination || emptyPagination);
      })
      .catch(() => {
        if (!mounted) return;
        if (isFirstPage) setProducts([]);
        setPagination(emptyPagination);
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
          setLoadingMore(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [page, queryString]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || loading || loadingMore || !pagination.hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !loading && !loadingMore && pagination.hasNextPage) {
          setPage((current) => current + 1);
        }
      },
      { rootMargin: "400px 0px" }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [loading, loadingMore, pagination.hasNextPage]);

  const updateTab = (tabKey) => {
    setActiveTab(tabKey);
    setPage(1);
    setProducts([]);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("page");
    if (tabKey === "all") nextParams.delete("section");
    else nextParams.set("section", tabKey);
    setSearchParams(nextParams);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
    setProducts([]);
  };

  const selectSubcategory = (subcategorySlug = "") => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("page");
    if (subcategorySlug) nextParams.set("subcategorySlug", subcategorySlug);
    else nextParams.delete("subcategorySlug");
    setSearchParams(nextParams);
    setPage(1);
    setProducts([]);
  };

  return (
    <section className="bg-[#f8fafc] py-10">
      <CmsEditBar editMode={cms.editMode} pageTitle="Products Listing Page" />
      <div className="container">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <CmsEditableText as="p" className="text-xs font-semibold uppercase tracking-[0.2em] text-[#3991D5]" editMode={cms.editMode} fieldKey="eyebrow" onSave={cms.updateField} sectionKey="header" value={cms.text("header", "eyebrow", "All Products")} />
            <CmsEditableText as="h1" className="mt-2 font-gopher text-4xl font-black uppercase text-[#0f172a] sm:text-5xl" editMode={cms.editMode} fieldKey="headline" multiline onSave={cms.updateField} sectionKey="header" value={cms.text("header", "headline", "Featured, popular, most viewed, and top selling products")} />
            <CmsEditableText as="p" className="mt-3 max-w-2xl text-sm font-semibold text-slate-500" editMode={cms.editMode} fieldKey="subtitle" multiline onSave={cms.updateField} sectionKey="header" value={cms.text("header", "subtitle", "Browse Bobbin USA industrial sewing parts, filter by category, and search by SKU, part name, or brand.")} />
            <p className="mt-3 text-sm font-semibold text-slate-500">
              {loading ? "Loading products..." : `${pagination.total} product${pagination.total === 1 ? "" : "s"} found`}
              {(categoryFilter || categorySlugFilter || subcategorySlugFilter || brandLineFilter) && ` / ${selectedSubcategory?.name || selectedCategory?.name || categoryFilter || categorySlugFilter || brandLineFilter}`}
            </p>
          </div>
          <input
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold outline-none focus:border-[#3991D5] lg:max-w-xs"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search parts, SKU, brand..."
            value={search}
          />
        </div>

        <div className="mb-4 flex gap-5 overflow-x-auto border-b border-slate-200">
          {TABS.map((tab) => (
            <button
              className={`relative whitespace-nowrap border-0 bg-transparent py-3 text-sm font-semibold ${activeTab === tab.key ? "text-[#3991D5]" : "text-slate-500"}`}
              key={tab.key}
              onClick={() => updateTab(tab.key)}
              type="button"
            >
              {tab.label}
              {activeTab === tab.key && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-[#3991D5]" />}
            </button>
          ))}
          {(categoryFilter || categorySlugFilter || subcategorySlugFilter || brandLineFilter || debouncedSearch) && (
            <button className="ml-auto whitespace-nowrap rounded-full bg-blue-50 px-3 py-2 text-xs font-black text-[#3991D5]" onClick={clearFilters} type="button">
              Clear filters
            </button>
          )}
        </div>

        {selectedCategory?.subcategories?.length > 0 && (
          <div className="mb-5 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#3991D5]">{selectedCategory.name}</p>
                <h2 className="mt-1 font-gopher text-xl font-black text-[#0f172a]">Subcategories</h2>
                {(selectedSubcategory?.description || selectedCategory.description) && (
                  <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
                    {selectedSubcategory?.description || selectedCategory.description}
                  </p>
                )}
              </div>
              <button
                className={`rounded-full px-4 py-2 text-xs font-black transition ${!subcategorySlugFilter ? "bg-[#3991D5] text-white" : "bg-blue-50 text-[#3991D5] hover:bg-blue-100"}`}
                onClick={() => selectSubcategory("")}
                type="button"
              >
                All {selectedCategory.name}
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
              {selectedCategory.subcategories.map((subcategory) => (
                <button
                  className={`overflow-hidden rounded-xl border bg-white text-left transition hover:-translate-y-0.5 hover:shadow-md ${subcategorySlugFilter === subcategory.slug ? "border-[#3991D5] shadow-md ring-2 ring-blue-100" : "border-blue-100 hover:border-[#3991D5]"}`}
                  key={subcategory.slug || subcategory.name}
                  onClick={() => selectSubcategory(subcategory.slug)}
                  type="button"
                >
                  <span className="flex h-28 w-full items-center justify-center border-b border-blue-50 bg-[#f8fbff] p-4">
                    <img alt="" className="max-h-full max-w-full object-contain" loading="lazy" src={resolveProductImage(subcategory.image)} />
                  </span>
                  <span className={`block px-3 py-3 text-sm font-black leading-snug ${subcategorySlugFilter === subcategory.slug ? "text-[#3991D5]" : "text-slate-700"}`}>
                    {subcategory.name}
                  </span>
                  {subcategory.description && (
                    <span className="block px-3 pb-3 text-xs font-semibold leading-5 text-slate-500">
                      {subcategory.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && page === 1 ? (
          <ProductGridSkeleton />
        ) : products.length ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
              {products.map((product) => (
                <ProductListingCard key={product._id || product.sku} product={product} />
              ))}
            </div>
            <div ref={loadMoreSentinelRef} className="py-8">
              {(loadingMore || pagination.hasNextPage) && (
                <div className="space-y-4">
                  {loadingMore && <ProductGridSkeleton count={6} />}
                  {!loadingMore && pagination.hasNextPage && (
                    <div className="flex items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-5 text-sm font-bold text-slate-500">
                      Scroll to load more products
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
            <p className="text-lg font-black text-slate-900">No products found</p>
            <p className="mt-2 text-sm font-semibold text-slate-500">Try a different category, brand, or search term.</p>
          </div>
        )}
      </div>
    </section>
  );
}

function ProductListingCard({ product }) {
  const { isSaved, loading, toggleWishlist } = useWishlistActions(product);
  const soldCount = getDisplaySoldCount(product);

  return (
    <Link
      className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:border-[#cfe6fa] hover:shadow-[0_20px_45px_rgba(15,23,42,0.10)]"
      to={`/products/${product.slug || product.sku}`}
    >
      <div className="relative flex h-52 items-center justify-center overflow-hidden bg-gradient-to-b from-white to-slate-50 p-4">
        <span className="absolute left-3 top-3 z-10 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#3991D5] shadow-sm">
          {product.brandLine || "Product"}
        </span>
        <button
          aria-label={isSaved ? "Remove from wishlist" : "Add to wishlist"}
          className={`absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border bg-white shadow-sm transition ${isSaved ? "border-red-100 text-red-500" : "border-slate-200 text-slate-400 hover:text-red-500"}`}
          disabled={loading}
          onClick={toggleWishlist}
          type="button"
        >
          <Heart className="h-4 w-4" fill={isSaved ? "currentColor" : "none"} />
        </button>
        <img alt={product.name} className="max-h-full max-w-full object-contain transition duration-300 group-hover:scale-[1.03]" src={resolveProductImage(product.image)} />
      </div>
      <div className="space-y-2 p-4">
        <h3 className="line-clamp-2 min-h-[2.75rem] font-gopher text-[15px] font-bold leading-5 text-[#1a202c]">{product.name}</h3>
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Stars count={Math.round(product.averageRating || product.rating || 4)} />
            <p className="text-[11px] font-semibold text-slate-400 mb-0">
              {Number(product.reviewCount || 0)} review{Number(product.reviewCount || 0) === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
          <span className="inline-flex items-center rounded-full bg-[#e0f4ff] px-3 py-1 text-lg font-bold text-[#3991D5]">${Number(product.price || 0).toFixed(2)}</span>
          {product.compareAtPrice && <span className="text-xs font-semibold text-slate-400 line-through">${Number(product.compareAtPrice).toFixed(2)}</span>}
          <div className="flex gap-1 items-center">
            <p className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400 mb-0">Sold</p>
            <p className="text-sm font-black text-slate-700 mb-0">{formatCount(soldCount)}</p>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ProductGridSkeleton({ count = 8 }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
      {Array.from({ length: count }).map((_, index) => (
        <div className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" key={index}>
          <div className="h-52 bg-slate-100" />
          <div className="space-y-3 p-4">
            <div className="h-4 rounded bg-slate-100" />
            <div className="h-4 w-3/4 rounded bg-slate-100" />
            <div className="h-7 w-24 rounded-full bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Stars({ count }) {
  return (
    <div className="flex items-center gap-1 text-amber-500">
      {Array.from({ length: 5 }).map((_, index) => (
        <Star
          className={index < count ? "h-3.5 w-3.5 fill-current" : "h-3.5 w-3.5 text-slate-300"}
          key={index}
        />
      ))}
    </div>
  );
}

export default AllProductsSection;
